import { useEffect, useMemo, useRef } from 'react';
import { getActionBody, getPrimaryQuickActions } from '@court-copilot/prompts';
import { Header } from '@/components/Header';
import { CommandOverlays } from '@/components/CommandOverlays';
import { OnboardingTour } from '@/components/OnboardingTour';
import { AnchorBanner } from '@/components/AnchorBanner';
import { WitnessPanel } from '@/components/WitnessPanel';
import { DocumentsQuickUpload } from '@/components/DocumentsQuickUpload';
import { UpcomingDeadlines } from '@/components/UpcomingDeadlines';
import { LiveAssistPanel } from '@/components/LiveAssistPanel';
import { MicHealthBanner } from '@/components/MicControls';
import { PressureTestPanel } from '@/components/PressureTestPanel';
import { ResponsePanel } from '@/components/ResponsePanel';
import { TranscriptPanel } from '@/components/TranscriptPanel';
import { SessionProvider, useSession } from '@/contexts/SessionContext';
import { TranscriptionProvider, useTranscriptionContext } from '@/contexts/TranscriptionContext';
import { HearingSessionProvider, useHearingSessionContext } from '@/contexts/HearingSessionContext';
import { useHotkeys, type HotkeyMap } from '@/hooks/useHotkeys';
import { useTranscriptSync } from '@/hooks/useTranscriptSync';
import { patchSessionTranscript } from '@/lib/api-client';
import { isPreHearingAcked } from '@/lib/prehearing';

interface HearingShellProps {
  matterId: string | null;
}

// Two-column hearing layout per 03-ui-design-language.md "Layout patterns".
// On large screens the transcript takes 3 of 5 columns and the response
// panel takes 2. Below lg the columns stack with transcript on top.
//
// Provider stack from outside in: Session (eager session per matter) ->
// TranscriptionWrapper (forwards initial transcript from the session into
// the recognition state) -> the body.
export function HearingShell({ matterId }: HearingShellProps) {
  return (
    <SessionProvider matterId={matterId}>
      <TranscriptionWrapper matterId={matterId}>
        <HearingSessionProvider>
          <div className="flex min-h-screen flex-col bg-stone-950">
            <Header />
            <MicHealthBanner />
            <div className="mx-auto w-full max-w-screen-2xl flex-1 px-5 py-4 sm:px-6">
              <div className="grid h-full grid-cols-1 gap-4 lg:grid-cols-5">
                <section className="lg:col-span-3">
                  <TranscriptPanel />
                </section>
                <section className="lg:col-span-2 space-y-3">
                  <AnchorBanner matterId={matterId} />
                  <WitnessPanel matterId={matterId} />
                  <DocumentsQuickUpload matterId={matterId} />
                  <UpcomingDeadlines compact />
                  {/* Friday Ready P1-4 + P1-5: rehearsal-only panels (both
                      render null outside rehearsal sessions). */}
                  <LiveAssistPanel />
                  <PressureTestPanel />
                  <ResponsePanel />
                </section>
              </div>
            </div>
          </div>
          <TranscriptSyncBridge />
          <CommandOverlays />
          <OnboardingTour />
        </HearingSessionProvider>
      </TranscriptionWrapper>
    </SessionProvider>
  );
}

// Wraps the body in TranscriptionProvider, seeded with the persisted
// transcript from the session. The provider key forces a remount when the
// session changes so the new initial value takes effect. matterId flows in so
// Deepgram can build per-matter keyterms (Sprint P5 Section 1).
function TranscriptionWrapper({
  children,
  matterId,
}: {
  children: React.ReactNode;
  matterId: string | null;
}) {
  const { sessionId, initialTranscript, initialTurns, initialSpeakerRoles } = useSession();
  return (
    <TranscriptionProvider
      key={sessionId ?? 'none'}
      initial={initialTranscript}
      matterId={matterId}
      sessionId={sessionId}
      initialTurns={initialTurns}
      initialSpeakerRoles={initialSpeakerRoles}
    >
      {children}
    </TranscriptionProvider>
  );
}

// Reads transcript and sessionId from the contexts, runs the 5s debounced
// PATCH so the server stays within seconds of the live state and reloads
// restore everything spoken so far. Also installs the F1-F6 hotkey handlers.
function TranscriptSyncBridge() {
  const { sessionId } = useSession();
  const { transcript, isListening, supported, userStopped, start, stop, reset, error, turns, speakerRoles } =
    useTranscriptionContext();
  const { ready, busy, ask } = useHearingSessionContext();

  // Always sync when there is any transcript, not only while listening:
  // the operator can stop listening, switch tabs, then return -- the latest
  // text should be saved.
  useTranscriptSync({ sessionId, transcript, turns, speakerRoles, enabled: true });

  // C.6 safeguard: transcription and the response stream run on fully
  // independent state, so firing a quick action never pauses listening. As a
  // backstop, if recognition does stop for any reason while a response is in
  // flight, note why and restart it the moment the response completes, so the
  // operator never loses a word mid query.
  const wasListeningAtQueryStart = useRef(false);
  const prevBusy = useRef(false);
  useEffect(() => {
    if (busy && !prevBusy.current) {
      wasListeningAtQueryStart.current = isListening;
    } else if (!busy && prevBusy.current) {
      // Only auto-restart if recognition died unexpectedly (silence timeout or a
      // recognition give-up) — NOT if the operator deliberately pressed stop
      // mid-response. Without the userStopped guard, a deliberate stop during a
      // query was overridden when the response completed (P4.1).
      if (wasListeningAtQueryStart.current && !isListening && supported && !userStopped) {
        console.warn(
          '[transcription] recognition stopped during a response; auto-restarting.',
          error ? `reason: ${error}` : 'reason: unknown (likely a silence timeout)',
        );
        start();
      }
    }
    prevBusy.current = busy;
  }, [busy, isListening, supported, userStopped, start, error]);

  // F1-F4 fire the primary quick actions in FAST mode; Shift+F1-F4 fire the DEEP
  // variants. F5 toggles listening; F6 clears the transcript (with the same eager
  // server flush as the Clear button).
  const primary = useMemo(() => getPrimaryQuickActions(), []);
  const hotkeyMap = useMemo<HotkeyMap>(() => {
    const map: HotkeyMap = {};
    for (const action of primary) {
      if (!action.hotkey) {
        continue;
      }
      const fnKey = action.hotkey as 'F1' | 'F2' | 'F3' | 'F4';
      map[fnKey] = () => {
        if (!ready) {
          return;
        }
        void ask(getActionBody(action, 'fast'), action.id, 'fast');
      };
      map[`Shift+${fnKey}`] = () => {
        if (!ready) {
          return;
        }
        void ask(getActionBody(action, 'deep'), `deep-${action.id}`, 'deep');
      };
    }
    map.F5 = () => {
      if (!supported) {
        return;
      }
      if (isListening) {
        stop();
      } else if (isPreHearingAcked(sessionId)) {
        // The first start of a session must go through the Start listening
        // button's lawful-use checklist (P5.4); F5 only starts once acknowledged.
        start();
      }
    };
    map.F6 = () => {
      if (transcript.length === 0) {
        return;
      }
      reset('');
      if (sessionId) {
        patchSessionTranscript(sessionId, '').catch((err: unknown) => {
          console.warn('hotkey clear save failed:', err);
        });
      }
    };
    return map;
  }, [
    primary,
    ready,
    ask,
    supported,
    isListening,
    start,
    stop,
    transcript.length,
    reset,
    sessionId,
  ]);

  useHotkeys(hotkeyMap);

  return null;
}
